import { useState, useCallback } from "react";
import type { ThemeConfig } from "antd";
import { lightTheme, darkTheme } from "../theme/antdTheme";

const STORAGE_KEY = "qb-dashboard-theme";

type ThemeMode = "light" | "dark";

export interface UseThemeReturn {
  isDark: boolean;
  toggleTheme: () => void;
  theme: ThemeConfig;
  mode: ThemeMode;
}

function getInitialMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  // Respect OS-level preference as fallback
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Hook that manages the application theme (light/dark).
 * Persists the user's preference in localStorage and respects the OS default
 * on first visit.
 */
export function useTheme(): UseThemeReturn {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  const isDark = mode === "dark";

  const toggleTheme = useCallback((): void => {
    setMode((current) => {
      const next: ThemeMode = current === "light" ? "dark" : "light";
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const theme: ThemeConfig = isDark ? darkTheme : lightTheme;

  return { isDark, toggleTheme, theme, mode };
}
